import React, { Component } from 'react';
import PropTypes from 'prop-types';

const TIMING = {
    MOMENTUM_INTERVAL: 50,
    KEY_REPEAT_INTERVAL: 100,
    SNAP_DELAY: 10,
    ANIMATION_BASE_DURATION: 500,
    ANIMATION_PER_TICK: 250,
    MAX_ANIMATION_DURATION: 2000,
};

const PHYSICS = {
    FRICTION: 0.94,
    MOMENTUM_THRESHOLD: 3,
    MIN_SPEED_THRESHOLD: 3,
};

const HEIGHTS = {
    MIN_MAX_TICK: 32,
    MEANINGFUL_INTERVAL_TICK: 18,
    REGULAR_TICK: 10,
    FILLER_TICK: 10,
    SELECTED_BONUS: 6,
};

export class AmountTicker extends Component {
    static propTypes = {
        options: PropTypes.arrayOf(PropTypes.number).isRequired,
        value: PropTypes.number.isRequired,
        onChange: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        
        this.state = {
            posOffset: 0,
            selectedIndex: props.options.indexOf(props.value),
            isDragging: false,
            dragSpeed: 0,
            initialized: false,
            dragStartX: 0,
            totalDragDistance: 0,
            keyHoldInterval: null,
            isAnimating: false,
        };

        // Refs
        this.tickerRef = React.createRef();
        this.targetRef = React.createRef();
        this.postMaxGroupRef = React.createRef();
        this.containerRef = React.createRef();
        this.timerRef = null;
        this.animationFrameRef = null;

        // Bind methods
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleTickClick = this.handleTickClick.bind(this);
        this.snapToIndex = this.snapToIndex.bind(this);
        this.setPosOffset = this.setPosOffset.bind(this);
        this.getClosestAmount = this.getClosestAmount.bind(this);
        this.handleMomentum = this.handleMomentum.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.moveToIndex = this.moveToIndex.bind(this);
        this.animateToIndex = this.animateToIndex.bind(this);
    }

    componentDidMount() {
        // Initialize to the selected value's index or max if not found
        const initialIndex = this.state.selectedIndex !== -1 ? this.state.selectedIndex : this.props.options.length - 1;
        setTimeout(() => {
            this.snapToIndex(initialIndex, false);
        }, TIMING.SNAP_DELAY);
        this.setState({ initialized: true });
        
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('keyup', this.handleKeyUp);
    }

    componentWillUnmount() {
        // Clean up to prevent memory leaks
        this.cleanup();

        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);

        if (this.state.keyHoldInterval) {
            clearInterval(this.state.keyHoldInterval);
        }
    }

    componentDidUpdate(prevProps) {
        // Handle external value changes
        if (prevProps.value !== this.props.value) {
            const newIndex = this.props.options.indexOf(this.props.value);
            if (newIndex !== -1 && newIndex !== this.state.selectedIndex) {
                this.snapToIndex(newIndex, false);
            }
        }
    }

    cleanup() {
        if (this.timerRef) {
            clearTimeout(this.timerRef);
            this.timerRef = null;
        }
        if (this.animationFrameRef) {
            cancelAnimationFrame(this.animationFrameRef);
            this.animationFrameRef = null;
        }
        document.removeEventListener('pointermove', this.handleMouseMove);
        document.removeEventListener('pointerup', this.handleMouseUp);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);
        // Clear any active key hold interval
        if (this.state.keyHoldInterval) {
            clearInterval(this.state.keyHoldInterval);
            this.setState({ keyHoldInterval: null });
        }
    }

    get tickerWidth() {
        return this.tickerRef.current ? this.tickerRef.current.scrollWidth : 0;
    }

    get extraWidth() {
        return (this.postMaxGroupRef.current ? this.postMaxGroupRef.current.scrollWidth : 0) + 26;
    }

    snapToIndex(index, callOnChange = true) {
        if (!this.tickerRef.current || !this.postMaxGroupRef.current || !this.targetRef.current) {
            return;
        }
        
        index = Math.max(0, Math.min(this.props.options.length - 1, index));

        const tickerRight = this.tickerRef.current.getBoundingClientRect().right;
        const dividers = this.tickerRef.current.querySelectorAll('.ticker-divider');
        const newValueDivider = dividers[index];
        
        if (!newValueDivider) {
            return;
        }

        const newOffset = tickerRight - 
            newValueDivider.getBoundingClientRect().right + 
            this.postMaxGroupRef.current.getBoundingClientRect().width + 26;

        this.setState({
            posOffset: newOffset,
            selectedIndex: index,
            dragSpeed: 0
        });

        if (callOnChange) {
            this.props.onChange(this.props.options[index]);
        }
    }

    setPosOffset(deltaOffset) {
        const minOffset = this.extraWidth - 50;
        const maxOffset = this.extraWidth + this.tickerWidth + 50;
        
        this.setState(prevState => {
            const newOffset = prevState.posOffset + deltaOffset;
            
            if (newOffset > maxOffset) {
                this.setState({ dragSpeed: 0 });
                this.snapToIndex(0);
                return { posOffset: maxOffset };
            } else if (newOffset < minOffset) {
                this.setState({ dragSpeed: 0 });
                this.snapToIndex(this.props.options.length - 1);
                return { posOffset: minOffset };
            } else {
                return { posOffset: newOffset };
            }
        });
    }

    getClosestAmount(snap = false) {
        if (!this.targetRef.current || !this.tickerRef.current || !this.postMaxGroupRef.current) {
            return;
        }

        const targetRight = this.targetRef.current.getBoundingClientRect().right;
        const dividers = this.tickerRef.current.querySelectorAll('.ticker-divider');

        let closestIndex = 0;
        let minDistance = Infinity;

        // Find the divider with minimum absolute distance to target
        for (let i = 0; i < dividers.length; i++) {
            const divider = dividers[i];
            const dividerRight = divider.getBoundingClientRect().right;
            const distance = Math.abs(dividerRight - targetRight);

            if (distance < minDistance) {
                minDistance = distance;
                closestIndex = i;
            }
        }

        const closestAmount = this.props.options[closestIndex];
        this.props.onChange(closestAmount);
        
        if (snap) {
            this.setState({ selectedIndex: closestIndex });
            this.snapToIndex(closestIndex, false);
        }
    }

    handleMomentum() {
        if (!this.state.initialized || this.state.isDragging || this.state.dragSpeed === 0) {
            return;
        }

        const newSpeed = this.state.dragSpeed * PHYSICS.FRICTION;

        if (Math.abs(newSpeed) < PHYSICS.MIN_SPEED_THRESHOLD) {
            this.setState({ dragSpeed: 0 });
            this.getClosestAmount(true);
            if (this.timerRef) {
                clearTimeout(this.timerRef);
                this.timerRef = null;
            }
        } else {
            this.setState({ dragSpeed: newSpeed });
            this.setPosOffset(newSpeed);
            this.getClosestAmount();
            
            this.timerRef = setTimeout(() => {
                this.handleMomentum();
            }, TIMING.MOMENTUM_INTERVAL);
        }
    }

    handleMouseMove(e) {
        e.preventDefault();
        const movementX = e.movementX || 0;
        this.setState(prevState => ({ 
            dragSpeed: movementX,
            totalDragDistance: prevState.totalDragDistance + Math.abs(movementX)
        }));
        this.setPosOffset(movementX);
        this.getClosestAmount();
    }

    handleMouseUp(e) {
        this.setState({ isDragging: false });
        document.removeEventListener('pointermove', this.handleMouseMove);
        document.removeEventListener('pointerup', this.handleMouseUp);
        
        // Check if there's sufficient momentum to continue scrolling
        const hasMomentum = Math.abs(this.state.dragSpeed) > PHYSICS.MOMENTUM_THRESHOLD;
        
        if (hasMomentum) {
            // Start momentum after a brief delay
            setTimeout(() => {
                this.handleMomentum();
            }, TIMING.SNAP_DELAY);
        } else {
            // No momentum, snap directly to nearest tick
            setTimeout(() => {
                this.getClosestAmount(true);
            }, TIMING.SNAP_DELAY);
        }
    }

    handleTickClick(tickIndex, e) {
        e.preventDefault();

        // Clean up any existing momentum or dragging (but not keyboard listeners)
        if (this.timerRef) {
            clearTimeout(this.timerRef);
            this.timerRef = null;
        }
        if (this.animationFrameRef) {
            cancelAnimationFrame(this.animationFrameRef);
            this.animationFrameRef = null;
        }
        document.removeEventListener('pointermove', this.handleMouseMove);
        document.removeEventListener('pointerup', this.handleMouseUp);
        
        // Clear any active key hold interval
        if (this.state.keyHoldInterval) {
            clearInterval(this.state.keyHoldInterval);
            this.setState({ keyHoldInterval: null });
        }
        
        this.setState({
            isDragging: false,
            dragSpeed: 0
        });

        // Animate to the clicked tick
        this.setState({ isAnimating: true });
        this.animateToIndex(tickIndex);
    }

    handleMouseDown(e) {
        if (this.state.isDragging) {
            return;
        }

        // Interrupt any ongoing click animation
        if (this.state.isAnimating) {
            cancelAnimationFrame(this.animationFrameRef);
            this.setState({ isAnimating: false });
        }

        e.preventDefault();
        this.setState({ 
            isDragging: true, 
            dragSpeed: 0,
            dragStartX: e.clientX,
            totalDragDistance: 0
        });

        // Clean up any existing momentum
        if (this.timerRef) {
            clearTimeout(this.timerRef);
            this.timerRef = null;
        }

        document.addEventListener('pointermove', this.handleMouseMove);
        document.addEventListener('pointerup', this.handleMouseUp);
    }

    moveToIndex(direction) {
        const { options, value } = this.props;
        
        const currentIndex = options.indexOf(value);
        if (currentIndex === -1) {
            return; // Current value not found in options
        }
        
        let newIndex;
        if (direction === 'left' && currentIndex > 0) {
            newIndex = currentIndex - 1;
        } else if (direction === 'right' && currentIndex < options.length - 1) {
            newIndex = currentIndex + 1;
        } else {
            return; // Can't move further in this direction
        }
        
        this.snapToIndex(newIndex, true);
    }

    animateToIndex(targetIndex, startTime = null) {
        if (!startTime) startTime = performance.now();
        
        const currentIndex = this.state.selectedIndex;
        const distance = Math.abs(targetIndex - currentIndex);
        const duration = Math.min(TIMING.ANIMATION_BASE_DURATION + (distance * TIMING.ANIMATION_PER_TICK), TIMING.MAX_ANIMATION_DURATION);
        
        const progress = Math.min((performance.now() - startTime) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        
        const intermediateIndex = Math.round(currentIndex + (targetIndex - currentIndex) * easedProgress);
        
        if (intermediateIndex !== this.state.selectedIndex) {
            this.snapToIndex(intermediateIndex, true);
        }
        
        if (progress < 1) {
            this.animationFrameRef = requestAnimationFrame(() => 
                this.animateToIndex(targetIndex, startTime)
            );
        } else {
            this.setState({ isAnimating: false });
        }
    }

    handleKeyDown(e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
            return;
        }

        e.preventDefault();

        if (this.state.keyHoldInterval) {
            return;
        }

        const direction = e.key === 'ArrowLeft' ? 'left' : 'right';
        
        this.moveToIndex(direction);
        
        const interval = setInterval(() => {
            this.moveToIndex(direction);
        }, TIMING.KEY_REPEAT_INTERVAL);
        
        this.setState({ keyHoldInterval: interval });
    }

    handleKeyUp(e) {
        // Only handle arrow keys
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
            return;
        }

        if (this.state.keyHoldInterval) {
            clearInterval(this.state.keyHoldInterval);
            this.setState({ keyHoldInterval: null });
        }
    }

    getMeaningfulIntervals(options) {
        // Don't show meaningful intervals tickks if less than 20 options
        if (options.length < 20) return [];
        
        const meaningfulIntervals = [];
        
        for (let i = 5; i < options.length; i += 5) {
            meaningfulIntervals.push(options[i]);
        }
        
        return meaningfulIntervals;
    }

    renderTickDividers() {
        const { options, value } = this.props;

        // Get meaningful intervals programmatically
        const meaningfulIntervals = this.getMeaningfulIntervals(options);

        return options.map((amt, i) => {
            const isMinOrMax = i === 0 || i === options.length - 1;
            
            const isMeaningfulInterval = meaningfulIntervals.includes(amt);
            
            // Check if we're far enough from Min/Max (more than 2 ticks away)
            const farFromMinMax = i > 2 && i < options.length - 4;
            
            const isSelected = amt === value;

            let height;
            if (isMinOrMax) {
                height = HEIGHTS.MIN_MAX_TICK;
            } else if (isMeaningfulInterval && farFromMinMax) {
                height = HEIGHTS.MEANINGFUL_INTERVAL_TICK;
            } else {
                height = HEIGHTS.REGULAR_TICK;
            }
            height += (isSelected ? HEIGHTS.SELECTED_BONUS : 0);
            
            const style = {
                width: '2px',
                height: `${height}px`,
                backgroundColor: '#000',
                borderRadius: '4px',
                transition: 'all 0.1s ease-out',
                position: 'relative',
                flexShrink: 0
            };

            // Add labels for min/max
            let afterContent = null;
            if (i === 0) {
                afterContent = (
                    <div style={{
                        position: 'absolute',
                        top: '38px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        Min
                    </div>
                );
            } else if (i === options.length - 1) {
                afterContent = (
                    <div style={{
                        position: 'absolute',
                        top: '38px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        Max
                    </div>
                );
            }

            return (
                <div key={amt} style={{ position: 'relative' }}>
                    {/* Visible tick */}
                    <div className="ticker-divider" style={style}>
                        {afterContent}
                    </div>
                    {/* Invisible click area */}
                    <div 
                        style={{
                            position: 'absolute',
                            top: '-8px',
                            left: '-8px',
                            width: '18px',
                            height: '58px',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            zIndex: 1
                        }}
                        onClick={(e) => {
                            // Only treat as click if drag distance is minimal
                            if (this.state.totalDragDistance < 5) {
                                this.handleTickClick(i, e);
                            }
                        }}
                    />
                </div>
            );
        });
    }

    renderFillerDividers(count = 12) {
        return Array.from({ length: count }).map((_, i) => (
            <div
                key={i}
                style={{
                    width: '2px',
                    height: `${HEIGHTS.FILLER_TICK}px`,
                    backgroundColor: '#ccc',
                    borderRadius: '4px',
                    flexShrink: 0
                }}
            />
        ));
    }

    handleLeftArrowClick = () => {
        if (this.state.selectedIndex > 0) {
            const newIndex = this.state.selectedIndex - 1;
            this.snapToIndex(newIndex, true);
        }
    };

    handleRightArrowClick = () => {
        if (this.state.selectedIndex < this.props.options.length - 1) {
            const newIndex = this.state.selectedIndex + 1;
            this.snapToIndex(newIndex, true);
        }
    };

    formatAmount(amount) {
        return `$${amount.toLocaleString()}`;
    }

    render() {
        const { posOffset, selectedIndex } = this.state;
        const { options, value } = this.props;
        
        const isAtMin = selectedIndex === 0;
        const isAtMax = selectedIndex === options.length - 1;

        const arrowHoverStyles = `
            .amount-ticker-arrow:not(.disabled):hover {
                background-color: rgba(0, 0, 0, 0.04) !important;
                transition: background-color 0.2s ease;
            }
            .amount-ticker-arrow:not(.disabled):focus-visible {
                background-color: rgba(0, 0, 0, 0.08) !important;
                outline: none;
                transition: background-color 0.2s ease;
            }
        `;

        const containerStyle = {
            flex: 1,
            height: '90px',
            overflowX: 'hidden',
            paddingTop: '16px',
            paddingBottom: '16px',
            cursor: 'pointer',
            position: 'relative',
            userSelect: 'none',
            touchAction: 'none',
            maskImage: 'linear-gradient(to right, transparent, black 50%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 50%, transparent)'
        };

        const sliderStyle = {
            display: 'flex',
            flexDirection: 'row',
            position: 'absolute',
            right: 'calc(51.8%)',
            gap: '16px',
            alignItems: 'center',
            height: '42px',
            transform: `translateX(${posOffset}px)`,
            transition: this.state.isDragging ? 'none' : 'transform 0.1s ease-out'
        };

        const fillerStyle = {
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            height: '42px',
            alignItems: 'center',
            flexWrap: 'nowrap'
        };

        const tickerStyle = {
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            height: '42px',
            flexWrap: 'nowrap',
            alignItems: 'center'
        };

        const targetStyle = {
            position: 'absolute',
            borderColor: '#000',
            left: '50%',
            height: '42px',
            width: '0',
            borderWidth: '2px',
            borderRadius: '4px',
            pointerEvents: 'none',
            borderStyle: 'solid'
        };

        const arrowStyle = {
            position: 'absolute',
            top: '15px',
            fontSize: '18px',
            color: '#000',
            cursor: 'pointer',
            userSelect: 'none',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
        };

        const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 550;
        
        const leftIsDisabled = isAtMin || !isDesktop;
        const rightIsDisabled = isAtMax || !isDesktop;
        
        const leftArrowStyle = {
            ...arrowStyle,
            left: 'calc(50% - 220px)',
            color: leftIsDisabled ? 'rgba(0, 0, 0, 0.26)' : '#000',
            pointerEvents: leftIsDisabled ? 'none' : 'auto',
        };

        const rightArrowStyle = {
            ...arrowStyle,
            right: 'calc(50% - 220px)',
            color: rightIsDisabled ? 'rgba(0, 0, 0, 0.26)' : '#000',
            pointerEvents: rightIsDisabled ? 'none' : 'auto',
        };

        return (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '548px', position: 'relative' }}>
                {isDesktop && <style dangerouslySetInnerHTML={{ __html: arrowHoverStyles }} />}
                {isDesktop && (
                    <div
                        className={`amount-ticker-arrow ${isAtMin ? 'disabled' : ''}`}
                        style={leftArrowStyle}
                        onClick={this.handleLeftArrowClick}
                        tabIndex={isAtMin ? -1 : 0}
                        role="button"
                        aria-label="Previous amount"
                    >
                        ←
                    </div>
                )}
                
                {isDesktop && (
                    <div
                        className={`amount-ticker-arrow ${isAtMax ? 'disabled' : ''}`}
                        style={rightArrowStyle}
                        onClick={this.handleRightArrowClick}
                        tabIndex={isAtMax ? -1 : 0}
                        role="button"
                        aria-label="Next amount"
                    >
                        →
                    </div>
                )}

                <div
                    ref={this.containerRef}
                    style={containerStyle}
                    onPointerDown={this.handleMouseDown}
                >
                    <div style={sliderStyle}>
                        {/* Filler dividers on left side */}
                        <div style={fillerStyle}>
                            {this.renderFillerDividers()}
                        </div>
                        
                        {/* Main ticker dividers */}
                        <div ref={this.tickerRef} style={tickerStyle}>
                            {this.renderTickDividers()}
                        </div>
                        
                        {/* Filler dividers on right side */}
                        <div ref={this.postMaxGroupRef} style={fillerStyle}>
                            {this.renderFillerDividers()}
                        </div>
                    </div>
                    
                    {/* Center target indicator */}
                    <div ref={this.targetRef} style={targetStyle} />
                </div>
            </div>
        );
    }
}
